// Environment validation utility for TellerWand
// This file validates required environment variables and provides helpful error messages

interface EnvironmentValidation {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * Validates all required environment variables for the TellerWand application
 * @returns EnvironmentValidation object with validation results
 */
export const validateEnvironment = (): EnvironmentValidation => {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Get values from environment variables (required for security)
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  const apiUrl = import.meta.env.VITE_API_URL || "https://tellerwand.com";

  // Required environment variables with fallbacks
  const requiredVars = {
    VITE_SUPABASE_URL: supabaseUrl,
    VITE_SUPABASE_ANON_KEY: supabaseAnonKey,
    VITE_API_URL: apiUrl,
  };

  // Check for missing required variables
  Object.entries(requiredVars).forEach(([key, value]) => {
    if (!value) {
      errors.push(`${key} is required but not set`);
    }
  });

  // Validate Supabase URL format
  if (supabaseUrl && !supabaseUrl.startsWith("https://")) {
    errors.push("VITE_SUPABASE_URL must start with https://");
  }

  // Validate API URL format
  if (apiUrl && !apiUrl.startsWith("http://") && !apiUrl.startsWith("https://")) {
    errors.push("VITE_API_URL must start with http:// or https://");
  }

  // Check for optional variables and provide warnings
  const optionalVars = {
    VITE_REALTIME_URL: import.meta.env.VITE_REALTIME_URL,
    VITE_STRIPE_PUBLIC_KEY: import.meta.env.VITE_STRIPE_PUBLIC_KEY,
    VITE_SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN,
  };

  Object.entries(optionalVars).forEach(([key, value]) => {
    if (!value) {
      warnings.push(`${key} is not set (optional)`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

/**
 * Logs environment validation results to console
 * @param validation EnvironmentValidation object
 */
export const logEnvironmentValidation = (validation: EnvironmentValidation): void => {
  if (import.meta.env.DEV) {
    if (validation.isValid) {
      console.warn("✅ All required environment variables are set");
    } else {
      console.error("❌ Environment validation failed:");
      validation.errors.forEach((error) => console.error(`  - ${error}`));
    }

    if (validation.warnings.length > 0) {
      console.warn("⚠️ Optional environment variables not set:");
      validation.warnings.forEach((warning) => console.warn(`  - ${warning}`));
    }
  }
};

/**
 * Throws an error if environment validation fails
 * @throws Error if required environment variables are missing
 */
export const assertEnvironment = (): void => {
  const validation = validateEnvironment();

  if (!validation.isValid) {
    const errorMessage = `Environment validation failed:\n${validation.errors.join("\n")}`;
    throw new Error(errorMessage);
  }
};

/**
 * Gets environment variable with fallback
 * @param key Environment variable key
 * @param fallback Fallback value if not set
 * @returns Environment variable value or fallback
 */
export const getEnvVar = (key: string, fallback?: string): string => {
  const value = import.meta.env[key];
  if (value !== undefined) {
    return value;
  }
  if (fallback !== undefined) {
    return fallback;
  }
  throw new Error(`Environment variable ${key} is required but not set`);
};

/**
 * Checks if the application is running in development mode
 * @returns true if in development mode
 */
export const isDevelopment = (): boolean => {
  return import.meta.env.DEV;
};

/**
 * Checks if the application is running in production mode
 * @returns true if in production mode
 */
export const isProduction = (): boolean => {
  return import.meta.env.PROD;
};

/**
 * True when the app is served from a local machine (Vite dev, preview, Electron devtools).
 */
export const isLocalHost = (hostname?: string): boolean => {
  const host = hostname ?? (typeof window !== "undefined" ? window.location.hostname : "");
  return host === "localhost" || host === "127.0.0.1" || host === "::1" || host === "[::1]";
};

/**
 * Whether client errors should be sent to Sentry (prod build on a real host only).
 */
export const shouldReportToSentry = (): boolean => {
  return isProduction() && !isLocalHost();
};
